import { LoyaltyChallengeConfig } from '@shared/models';

import {
  DeviceType,
  DISPLAY_CONDITION,
  NavbarItemLocation,
  ProductCheckTabType,
  RewardFormat,
  SubType,
  TitleType,
  Variants,
} from '../utils';

export const LYNX_CONFIG: LoyaltyChallengeConfig = {
  currency: 'USD',
  appIcon: 'assets/images/lynx/favicon.ico',
  cssVariables: {
    'font-family-medium': 'Montserrat-Medium',
    'font-family-bold': 'Montserrat-Bold',
    'font-family-extra-bold': 'Montserrat-ExtraBold',
    'font-family-black': 'Montserrat-Black',
    'font-family-semiBold': 'Montserrat-SemiBold',

    'navbar-height': '73px',
    'navbar-background-color': '#ffffff',

    'nav-link-color': '#707070',
    'nav-link-hover-color': '#272F35',
    'nav-link-active-color': '#D82117',

    'nav-item-bg-color': 'white',
    'nav-item-active-bg-color': 'white',
    'nav-item-hover-bg-color': 'rgb(0 0 0 / 10%)',
    'nav-item-font-family': 'var(--font-family-extra-bold)',

    'body-color': '#000000',
    'body-background-color': '#ffffff',

    'challenge-primary-color': '#D82117',
    'tooltip-text-color': 'white',
    'tooltip-background-color': 'black',
  },
  rewardFormat: RewardFormat.PRICE,
  og: {
    title: 'Hy-Vee Bonus Rewards | Select Offers, Earn PERKS Rewards',
    description:
      'Select Bonus Reward offers to play and earn PERKS Rewards to spend in-store or online. Earn up to $50 each month when you shop your favorite brands with your Hy-Vee PERKS card.',
  },
  rewardTemplate:
    '{points}<div style="font-family: var(--font-family-bold); color: white; font-size: 15px; margin-top: 0.125rem;">points</div>',
  pages: {
    availablePages: ['current', 'help'],
    help: {
      meta: {
        title: 'Hy-Vee Bonus Rewards — Help Center & FAQs',
        keywords:
          'Hy-Vee, Bonus Rewards, Bonus Reward offers, PERKS Rewards, Hy-Vee PERKS card, how to earn PERKS Rewards, spend PERKS Rewards in-store or online, Bonus Rewards FAQs, loyalty rewards help',
        description:
          'Find help with Hy-Vee Bonus Rewards — learn how to select Bonus Reward offers, earn PERKS Rewards with your Hy-Vee PERKS card, and spend them in-store or online.',
      },
      header: {
        background: '#5C0002',
        bannerAlternativeText: '',
        goBackButton: {
          text: 'Go Back To My Bonus Rewards',
          backgroundColor: '#D82117',
          color: 'white',
          fontFamily: 'var(--font-family-extra-bold)',
          borderRadius: '100px',
        },
        howToPlay: {
          specificDisplayCondition: DISPLAY_CONDITION.ALWAYS,
        },
      },
      faqVariant: {
        variant: Variants.VARIANT_1,
        title: {
          displayWhenCampaignBis: false,
          label: 'Frequently Asked Questions',
          type: TitleType.TEXT,
          styles: {
            color: '#D82117',
            fontSize: '25px',
            fontFamily: 'var(--font-family-bold)',
            textAlign: 'center',
          },
        },
        questions: [
          {
            title: 'How many Bonus Rewards can I activate at one time?',
            content: 'You can activate up to 10 Bonus Rewards each month.',
          },
          {
            title: 'Can I change which Bonus Rewards I activate?',
            content:
              'Yes, you can change which Bonus Rewards you activate, as long as you haven’t started spending toward them. Once you’ve made a qualifying purchase toward a Bonus Reward, it can’t be deactivated.',
          },
          {
            title: 'How do I earn PERKS Rewards?',
            content:
              'Once you activate a Bonus Reward, you can make qualifying purchases online or in store — just make sure you’re logged into your Hy-Vee account online, or scan your Hy-Vee PERKS card at checkout in store. You’ll earn PERKS Rewards based on your progress, even if the Bonus Reward isn’t fully completed.',
          },
          {
            title: 'How are my PERKS Rewards calculated?',
            content:
              'As you purchase eligible products, your progress increases toward each Bonus Reward. Each time you reach a milestone within a Bonus Reward, you’ll earn PERKS Rewards until the maximum earnings balance is reached.',
          },
          {
            title:
              'If I spend more than a Bonus Reward requires, can I increase my PERKS Reward amount?',
            content:
              'No. Each Bonus Reward has a set maximum reward, so spending more than required won’t increase your PERKS Reward amount.',
          },
          {
            title:
              'Which purchases will count toward my activated Bonus Rewards?',
            content:
              'Qualifying purchases count when you scan your Hy-Vee PERKS card in store or are signed in to your Hy-Vee account when placing online orders. Purchasing <a href="https://www.hy-vee.com/corporate/policy/bonus-perks/" target="_blank"> eligible products</a> that are included in an activated Bonus Reward will count toward your earnings progress.',
          },
          {
            title: 'How can I follow the progress of my Bonus Rewards?',
            content:
              'Your Bonus Rewards progress will update within 2 hours after an in-store, pickup, or delivery purchase. Each Bonus Reward has its own progress bar, so you can track your progress through the end of the Bonus Reward period.',
          },
          {
            title:
              'How soon after I make a qualified purchase will I see progress toward my Bonus Rewards?',
            content:
              'It takes about 2 hours for your Bonus Rewards progress to update online or in the app after a purchase has been made in store, or an online order has been picked up or delivered. If you made an in-store purchase or had an online order fulfilled today, check back in 2 hours to see your updated progress.',
          },
          {
            title: 'Can I combine my Bonus Rewards with other discounts?',
            content:
              'Yes, Bonus Rewards can be combined with most promotions. When a discount applies, the qualifying amount you spend after the discount applies will count as spend toward the Bonus Reward. Note that some discounts, like the employee discount come off of all eligible items in your order, so the amount qualifying for the Bonus Reward may be less than the price of the item on the shelf.',
          },
          {
            title: 'How do I use my PERKS Rewards?',
            content:
              'You can redeem your PERKS Rewards at participating Hy-Vee, Fast & Fresh and Dollar Fresh store locations on qualifying in-store purchases when you scan your Hy-Vee PERKS card at checkout, or online when you’re signed in to your Hy-Vee account. PERKS Rewards will be applied automatically to your next qualifying purchase. <a href="https://www.hy-vee.com/corporate/policy/bonus-perks/" target="_blank">Some exclusions apply</a>.',
          },
          {
            title:
              'When will my PERKS Rewards be available on my Hy-Vee PERKS card?',
            content:
              'PERKS Rewards will be automatically added to your Hy-Vee PERKS card on the second day of each month following the end of the calendar month in which they were earned. You can view your Bonus Rewards balance online or in the PERKS Hub located in your Hy-Vee app.',
          },
          {
            title: 'When do my PERKS Rewards expire?',
            content:
              'Your PERKS Rewards will expire 30 days after they’ve been added to your account.',
          },
        ],
        accordion: {
          styles: {
            title: {
              fontSize: '14px',
              fontFamily: 'var(--font-family-medium)',
              color: '#272F35',
              lineHeight: '22px',
              margin: '10px 0',
            },
            description: {
              fontSize: '12px',
              color: '#272F35',
              fontFamily: 'var(--font-family-medium)',
              lineHeight: '20px',
              margin: '5px 5px',
            },
          },
          alternativeTextExpandMore: '',
          alternativeTextExpandLess: '',
        },
      },
      extraLinks: [
        {
          title: {
            text: 'Need more help?',
            fontFamily: 'var(--font-family-bold)',
          },
          link: {
            color: 'white',
            label: 'Contact us',
            fontFamily: 'var(--font-family-medium)',
            backgroundColor: '#D82117',
            href: 'https://www.hy-vee.com/pages/contact',
          },
        },
      ],
    },
    currentChallenges: {
      meta: {
        title: 'My Hy-Vee Bonus Rewards | Select Offers & Earn PERKS Rewards',
        keywords:
          'Hy-Vee, Bonus Rewards, Bonus Reward offers, PERKS Rewards, Hy-Vee PERKS card, select Bonus Reward offers, earn PERKS Rewards, spend in-store or online, earn $50, monthly rewards',
        description:
          'Select Bonus Reward offers to play and earn PERKS Rewards to spend in-store or online. Earn up to $50 each month when you shop your favorite brands with your Hy-Vee PERKS card.',
      },
      background: 'white',
      howDoesitWork: {
        color: 'white',
        fontFamily: 'var(--font-family-bold)',
        backgroundColor: '#D82117',
        containerBackgroundColor: 'white',
        text: 'How it works',
        borderRadius: '100px',
        containerPadding: '2rem 0 0',
        modal: {
          background: '#FFFFFF',
          title: {
            text: 'How it works',
            color: '#000000',
            fontFamily: 'var(--font-family-bold)',
          },
          closeButton: {
            imageUrl: 'assets/images/default/close-button.svg',
            alternativeText: 'Close the tutorial',
            color: 'var(--challenge-primary-color)',
          },
          skipNowButton: {
            text: 'Skip for now',
            color: 'white',
            fontFamily: 'var(--font-family-bold)',
            backgroundColor: '#D82117',
            borderRadius: '100px',
          },
        },
      },
      howToPlay: {
        specificDisplayCondition: DISPLAY_CONDITION.DEFAULT,
      },
    },
    sorry: {
      background: '#F7F5EB',
      content: [
        '<div style="margin-bottom: 1rem;"><img src="assets/images/lynx/logo.svg" alt="Hy Vee logo"></div>',
        '<div style="font-size: 1.5rem; text-align: center; color: Black; font-family: var(--font-family-bold);">Sorry, this site is temporarily unavailable.</div>',
      ],
    },
  },
  components: {
    header: {
      variant: Variants.VARIANT_9,
      backgroundColor: '#D82117',
      bannerAriaLabel:
        'HyVee Bonus Rewards banner: Earn up to $50 in rewards with your favorite brands',
      backgroundImage: {
        mobile: {
          imageUrl: 'assets/images/lynx/mobile-cover.webp',
        },
        tablet: {
          imageUrl: 'assets/images/lynx/tablet-cover.webp',
        },
        desktop: {
          imageUrl: 'assets/images/lynx/desktop-cover.webp',
        },
      },
      CampaignDate: {
        color: 'white',
        fontFamily: 'var(--font-family-semiBold)',
        template: 'From {startDate} to {endDate}',
      },
      retailerLogo: {
        imageUrl: 'assets/images/lynx/retailer-logo-white.svg',
        alternativeText: 'Lynx logo',
      },
      primaryLabel: {
        color: 'white',
        fontFamily: 'var(--font-family-black)',
        text: 'Bonus \n Rewards',
      },
      gotoButton: {
        color: 'white',
        label: 'Go to my Bonus Rewards',
        fontFamily: 'var(--font-family-bold)',
        backgroundColor: '#5C0002',
        iconUrl: 'assets/images/lynx/header-button-icon.svg',
      },
    },
    brands: {
      variant: Variants.VARIANT_3,
      backgroundColor: 'none',
      brandsBgColor: 'white',
      border: '1.5px solid #EFEFEF',
      label: {
        text: 'Get rewarded for buying the brands you love.',
        color: 'white',
        fontFamily: 'var(--font-family-semiBold)',
        textTransform: 'none',
      },
    },
    footer: {
      variant: Variants.VARIANT_1,
      mobileVisiblity: true,
      showInIframe: true,
      styles: {
        display: 'flex',
        padding: '30px 0',
        alignItems: 'center',
        flexDirection: 'row',
        justifyContent: 'space-evenly',
        backgroundColor: '#000000',
      },
      linkStyles: {
        color: 'white',
        fontSize: '16px',
        textDecoration: 'none',
        fontFamily: 'var(--font-family-extra-bold)',
      },
      links: [
        {
          url: 'help',
          label: 'Help Center',
        },
        {
          external: true,
          url: 'https://www.hy-vee.com/corporate/policy/bonus-perks',
          label: 'Terms and Conditions',
        },
      ],
    },
    tutorial: {
      background: 'rgba(92, 0, 2, 1)',
      carousel: {
        config: {
          sectionAriaLabel: 'Tutorial carousel',
          controlsAriaLabel: 'Carousel controls',
          paginationAriaLabel: 'Slide indicators',
          title: {
            fontSize: '18px',
            color: 'white',
            text: 'HOW IT WORKS',
            fontFamily: 'var(--font-family-extra-bold)',
          },
          dots: {
            activeBg: '#DBDBDB',
            inactiveBg: 'white',
            ariaLabel: 'Go to slide {variable} of {variable}',
          },
          slideIndicatorLabel: 'Slide {variable} on {variable}',
          controls: {
            leftIcon: {
              imageUrl: 'assets/images/lynx/chevron-left.svg',
              alternativeText: 'Go to the previous slide',
            },
            rightIcon: {
              imageUrl: 'assets/images/lynx/chevron-right.svg',
              alternativeText: 'Go to the next slide',
            },
          },
        },
        items: {
          variant: Variants.VARIANT_3,
          styles: {
            title: {
              color: '#4A4A4A',
              fontFamily: 'var(--font-family-extra-bold)',
            },
            description: {
              color: '#4A4A4A',
              fontFamily: 'var(--font-family-medium)',
            },
            card: {
              border: 'none',
              boxShadow: 'none',
              background: 'white',
              borderRadius: '10px',
            },
          },
          steps: [
            {
              title: '1. ACTIVATE',
              description: 'Select up to {variable} Bonus Rewards each month.',
              picture: {
                imageUrl: 'assets/images/lynx/step-1.webp',
                alternativeText:
                  'Select up to {variable} PERKS Rewards each month.',
              },
            },
            {
              title: '2. SHOP',
              description:
                'Scan your PERKS card in store or log in when shopping online.',
              picture: {
                imageUrl: 'assets/images/lynx/step-2.webp',
                alternativeText:
                  'Scan your PERKS card in store or log in when shopping online.',
              },
            },
            {
              title: '3. EARN',
              description:
                'Spend toward each Bonus Reward and earn up to $50 in PERKS Rewards next month.',
              picture: {
                imageUrl: 'assets/images/lynx/step-3.webp',
                alternativeText:
                  'Spend toward each Bonus PERK and earn up to $50 in PERKS Rewards next month.',
              },
            },
          ],
        },
      },
    },
    wallet: {
      variant: Variants.VARIANT_2,
      backgroundColor: '#5C0002',
      label: {
        color: 'white',
        fontSize: '10px',
        text: 'Rewards In Progress',
        fontFamily: 'var(--font-family-medium)',
        lineHeight: '24px',
      },
      amount: {
        color: 'white',
        fontFamily: 'var(--font-family-extra-bold)',
      },
    },
    navbar: {
      variant: Variants.VARIANT_1,
      menuVariant: Variants.VARIANT_2,
      hasLayoutPaddingTop: true,
      dropdownLabel: 'Menu',
      fontFamily: 'var(--font-family-extra-bold)',
      openButton: {
        imageUrl: 'assets/images/lynx/menu.svg',
        buttonTitle: 'Click to see more options',
        imageAlt: 'Click to see more options',
      },
      logo: {
        url: 'current',
        height: '50px',
        imageUrl: 'assets/images/lynx/logo.svg',
        alternativeText: 'HyVee logo',
      },
      items: [
        {
          url: 'current',
          label: 'My Bonus Rewards',
          ariaLabel: 'Go to the HyVee Bonus Rewards page',
          displayOn: [NavbarItemLocation.MOBILE, NavbarItemLocation.DESKTOP],
        },
        {
          url: 'help',
          label: 'How It Works',
          ariaLabel: 'Go to the How It Works page',
          displayOn: [NavbarItemLocation.MOBILE, NavbarItemLocation.DESKTOP],
        },
      ],
    },
    countdown: {
      variant: Variants.VARIANT_1,
      showOnNavbarWhen: [],
      showAfterCampaignSummaryWhen: [],
      showBeforeCampaignSummaryWhen: [DeviceType.TABLET, DeviceType.MOBILE],
      showOnPages: 'all',
      titleAppearing: ['column', 'column', 'row'],
      titleVerticalAlignment: ['center', 'center', 'center'],
      labelStyle: {
        color: '#272F35',
        fontFamily: 'var(--font-family-semiBold)',
      },
      title: {
        color: '#272F35',
        showTitleOnMobile: true,
        fontFamily: 'var(--font-family-semiBold)',
        label: 'Current Bonus <br/>Rewards end in',
      },
      numberStyle: {
        color: 'white',
        backgroundColor: '#D82117',
        fontFamily: 'var(--font-family-extra-bold)',
      },
      texts: {
        days: 'days',
        hours: 'hours',
        minutes: 'minutes',
        seconds: 'seconds',
      },
    },
    challenges: {
      elements: {
        winCard: {
          variant: Variants.VARIANT_4,
          backgroundColor: '#D82117',
          left: {
            backgroundImage: {
              imageUrl: 'assets/images/lynx/reward-container.svg',
              alternativeText:
                'Congratulations! You earned ${variable} by completing this Bonus Reward!',
            },
            amount: {
              color: 'white',
              fontFamily: 'var(--font-family-bold)',
            },
          },
          right: {
            winFirstLabel: {
              text: 'Bonus Reward',
              color: 'white',
              fontFamily: 'var(--font-family-extra-bold)',
            },
            winSecondLabel: {
              text: 'Completed!',
              color: 'white',
              fontFamily: 'var(--font-family-extra-bold)',
            },
          },
        },
        selectionButton: {
          variant: Variants.VARIANT_3,
          buttonCatchPhrase: {
            buttonLabel: 'Activate',
            buttonBackgroundColor: '#D82117',
            buttonColor: 'white',
            buttonBorderRadius: '100px',
            buttonFontFamily: 'var(--font-family-extra-bold)',
          },
          catchPhrase: {
            selectImage: {
              imageUrl: 'assets/images/lynx/reward-container.svg',
              alternativeText:
                'Select this Bonus Reward and collect up to ${variable}',
            },
            amountFontFamily: 'var(--font-family-bold)',
            amountColor: 'white',
          },
        },
        unselectionButton: {
          variant: Variants.VARIANT_1,
          icon: {
            imageUrl: 'assets/images/lynx/cancel.svg',
            alternativeText: 'Cancel this Bonus Reward',
          },
        },
        outOfStock: {
          variant: Variants.VARIANT_1,
          backgroundColor: 'rgba(0, 0, 0, 0.65)',
          borderRadius: '16px',
          banner: {
            textColor: 'white',
            borderRadius: '4px',
            backgroundColor: '#DB3832',
            text: 'This Bonus Reward went fast!',
            fontFamily: 'var(--font-family-extra-bold)',
          },
        },
      },
      step: {
        [SubType.DEFAULT]: {
          variant: Variants.VARIANT_3,
          bannerAlternativeText: 'My Bonus Reward banner',
          logoAlternativeText: 'Logo banner',
          selectionButtonbackgroundColor: 'white',
          header: {
            height: '143px',
            backgroundColor: '#FFFFFF',
          },
          subHeader: {
            color: '#242729',
            completedBackgroundColor: '#D82117',
            backgroundColor: 'white',
            fontFamily: 'var(--font-family-semiBold)',
            fontSize: '11px',
            icon: {
              title: 'Click to see the list of products',
              displayWhenSelected: false,
              imageUrl: 'assets/images/lynx/info-icon.svg',
              alternativeText: 'Click to see the list of products',
            },
          },
          body: {
            progressbar: {
              earnLabel: 'Rewards',
              spendLabel: 'Spend',
              labelColor: '#242729',
              progressBarLeft: '54px',
              progressColor: '#D82117',
              containerColor: '#F2F2F2',
              labelFontFamily: 'var(--font-family-bold)',
              pictureStageReached: {
                imageUrl: 'assets/images/lynx/picture-reached.svg',
                alternativeText:
                  'Step reached: Mark this Bonus Reward step as completed',
              },
            },
            rewards: {
              activeLabel: 'Earn',
              passedLabel: 'Earned',
              flexDirection: 'column-reverse',
              labelFontFamily: 'var(--font-family-bold)',
              color: '',
              fontSize: '15px',
              fontFamily: 'var(--font-family-black)',
              backgroundColors: {
                bubbleWidth: '65px',
                bubbleHeight: '56px',
                reached: {
                  fill: '#7B1315',
                  stroke: '#D82117',
                  textColor: 'white',
                  labelColor: 'white',
                },
                unreached: {
                  fill: 'white',
                  stroke: '#7B1315',
                  textColor: '#7B1315',
                  labelColor: '#7B1315',
                },
                default: {
                  fill: '#7B1315',
                  stroke: '#D82117',
                  textColor: 'white',
                  labelColor: 'white',
                },
                backgroundImage: 'assets/images/lynx/max-reward.svg',
                alternativeText: {
                  reached: 'I earned ${variable}',
                  default: 'I earned ${variable}',
                  unreached: 'Earn ${variable}',
                },
              },
            },
            targets: {
              color: '#707070',
              fontFamily: 'var(--font-family-bold)',
              textDecorationOnReached: 'line-through',
            },
          },
          footer: {
            color: '#707070',
            label: 'Total Spend',
            fontFamily: 'var(--font-family-bold)',
            icon: {
              imageUrl: 'assets/images/lynx/shop.svg',
              alternativeText: 'Cart icon',
            },
            spentAmountFontFamily: 'var(--font-family-bold)',
            creditInfo: {
              color: '#242729',
              label: 'Up to 2h to reflect',
              fontFamily: 'var(--font-family-medium)',
            },
          },
        },
      },
      frequency: {
        [SubType.DEFAULT]: {
          variant: Variants.VARIANT_1,
          boxShadow: 'rgba(38, 38, 38, 0.08) 0px 4px 24px',
          header: {
            backgroundImage: {
              url: 'assets/images/default/frequency-banner.webp',
              bannerAriaLabel: 'Frequency Challenge',
            },
            height: '80px',
          },
          subHeader: {
            borderBottom: '1px solid #f4f5f6',
            completedBackgroundColor: '#D82117',
            backgroundColor: 'white',
            title: {
              color: 'black',
              label: 'Visits challenge',
              fontFamily: 'var(--font-family)',
            },
          },
          body: {
            label: 'Minimum amount of {variable}€ per visit',
            labelFontFamily: 'var(--font-family-bold)',
            labelFontSize: '14px',
            stamps: {
              showNumbers: false,
              iconSelected: 'assets/images/default/frequency-stamp-success.svg',
              iconNonSelected:
                'assets/images/default/frequency-stamp-empty.svg',
            },
          },
          footer: {
            color: 'black',
            label: 'Visits: ',
            fontFamily: 'var(--font-family)',
            footerIcon: {
              imageUrl: 'assets/images/default/shop.svg',
              alternativeText: '',
            },
          },
        },
      },
    },
    challengeSummary: {
      variant: Variants.VARIANT_1,
      stackOnMobile: true,
      title: {
        color: '#242729',
        text: 'My Bonus Rewards',
        fontFamily: 'var(--font-family-extra-bold)',
        transform: 'none',
      },
      challengeLabels: {
        inProgress: {
          color: '#000000',
          text: 'In Progress',
          fontFamily: 'var(--font-family-semiBold)',
        },
        onGoingLabel: {
          color: '#000000',
          text: 'Activated',
          fontFamily: 'var(--font-family-semiBold)',
        },
        completedLabel: {
          color: '#333333',
          text: 'Completed',
          fontFamily: 'var(--font-family-semiBold)',
        },
      },
      counterBorderRadius: '5px',
      challengesInfos: {
        color: '#5C0002',
        backgroundColor: '#F7F5EB',
        fontFamily: 'var(--font-family-black)',
      },
      update: {
        color: '#242729',
        text: 'Last update: {variable}',
        defaultFontSize: '16px',
        fontFamily: 'var(--font-family-semiBold)',
        dateFormat: 'MM/dd/yyyy',
      },
    },
    modals: {
      closeButton: {
        label: 'Close',
        textColor: 'white',
        borderRadius: '80px',
        backgroundColor: '#D82117',
        fontFamily: 'var(--font-family-extra-bold)',
        ariaLabel: 'Close this dialog',
      },
      unselectChallenge: {
        backgroundColor: '#5C0002',
        header: {
          backgroundColor: '',
          backgroundImage: '',
        },
        frequencyHeaderImages: {
          [SubType.DEFAULT]: {
            imageUrl: 'assets/images/default/frequency-header.webp',
            alternativeText: '',
          },
        },
        title: {
          color: 'white',
          fontFamily: 'var(--font-family-extra-bold)',
          label: 'Are you sure you want to<br/> unselect this Bonus Reward?',
        },
        body: {
          color: 'white',
          fontFamily: 'var(--font-family)',
          label:
            'No worries! If you change your mind,<br/> you can activate this Bonus Reward again.',
        },

        actionButtons: {
          variant: Variants.VARIANT_1,
          confirmButton: {
            label: 'Yes',
            borderRadius: '50px',
            textColor: '#FFFFFF',
            backgroundColor: '#D82117',
            fontFamily: 'var(--font-family-extra-bold)',
            ariaLabel: 'Confirm cancelling the Bonus Reward',
          },
          closeButton: {
            label: 'No',
            borderRadius: '50px',
            textColor: '#707070',
            backgroundColor: 'white',
            borderColor: '#707070',
            fontFamily: 'var(--font-family-extra-bold)',
            ariaLabel: 'Cancel the action and return to the Bonus Reward',
          },
        },
      },
      productCheck: {
        ariaLabel: '',
        backgroundColor: '#FFFFFF',
        fontFamily: 'var(--font-family-medium)',
        tabs: [
          {
            type: ProductCheckTabType.PRODUCT_LIST,
            label: 'Enter manually',
            searchable: true,
            searchByLabel: true,
            inputPlaceholder: 'Search by product name or barcode',
            tableTitle: {
              text: 'List of eligible products',
              color: 'var(--challenge-primary-color)',
            },
            columns: {
              name: {
                header: 'Product',
                color: '#000000',
                fontFamily: 'var(--font-family-medium)',
              },
              code: {
                header: 'Barcode',
                color: '#000000',
                fontFamily: 'var(--font-family-medium)',
              },
            },
          },
        ],
      },
      firstSelection: {
        backgroundColor: '#5C0002',
        title: {
          color: 'white',
          label: 'Bonus Reward activated',
          fontFamily: 'var(--font-family-extra-bold)',
        },
        header: {
          backgroundImage: '',
          backgroundColor: '#5C0002',
          logo: {
            imageUrl: 'assets/images/lynx/first-selection.svg',
            alternativeText: 'You have selected the first Bonus Rewards',
          },
        },
        body: {
          color: 'white',
          fontFamily: 'var(--font-family-medium)',
          label: 'Nice choice!<br/> You can activate 9 more Bonus Rewards.',
        },
      },
      lastSelection: {
        backgroundColor: '#5C0002',
        title: {
          color: 'white',
          fontFamily: 'var(--font-family-extra-bold)',
          label: "That's {variable} Bonus Rewards",
        },
        header: {
          backgroundImage: '',
          backgroundColor: '#5C0002',
          logo: {
            imageUrl: 'assets/images/lynx/last-selection.svg',
            alternativeText: 'You have selected the last Bonus Rewards',
          },
        },
        body: {
          color: 'white',
          fontFamily: 'var(--font-family-medium)',
          label:
            'Shop online or in store with your PERKS card to hit milestones and earn up to $50.',
        },
      },
      maximumSelection: {
        backgroundColor: '#5C0002',
        title: {
          color: 'white',
          fontFamily: 'var(--font-family-extra-bold)',
          label: 'Sorry, you have already selected {variable} Bonus Rewards',
        },
        header: {
          backgroundImage: '',
          backgroundColor: '#5C0002',
          logo: {
            imageUrl: 'assets/images/lynx/maximum-selection.svg',
            alternativeText: '',
          },
        },
        body: {
          color: 'white',
          fontFamily: 'var(--font-family-medium)',
          label:
            'Don’t worry! You can deselect a Bonus Reward that you haven’t started to activate a different one.',
        },
      },
      outOfStockIssue: {
        backgroundColor: '#5C0002',
        title: {
          color: 'white',
          fontFamily: 'var(--font-family-extra-bold)',
          label: 'Limit reached',
        },
        body: {
          color: 'white',
          fontFamily: 'var(--font-family-medium)',
          label:
            'This Bonus Reward has reached the maximum number of participants. Please choose another.',
        },
      },
      genericError: {
        backgroundColor: '#5C0002',
        title: {
          color: 'white',
          fontFamily: 'var(--font-family-extra-bold)',
          label: 'Error',
        },
        body: {
          color: 'white',
          fontFamily: 'var(--font-family-medium)',
          label:
            "An error occurred, and the Bonus Reward couldn't be selected. Please check your connection and try again.",
        },
      },
      timeoutError: {
        backgroundColor: '#5C0002',
        title: {
          color: 'white',
          fontFamily: 'var(--font-family-extra-bold)',
          label: 'Request timed out',
        },
        body: {
          color: 'white',
          fontFamily: 'var(--font-family-medium)',
          label:
            'Selecting the Bonus Reward took too long. Please check your connection and try again.',
        },
      },
      challengeUnavailable: {
        backgroundColor: '#5C0002',
        title: {
          color: 'white',
          fontFamily: 'var(--font-family-extra-bold)',
          label: 'Campaign ended',
        },
        body: {
          color: 'white',
          fontFamily: 'var(--font-family-medium)',
          label:
            'This campaign has ended and Bonus Rewards are no longer available for selection.',
        },
      },
    },
  },
};
